import formatDimension from "./formatDimension.ts";
import { t, useStoreLanguage, getDirection, getLanguage, formatRugCount, formatSizeCount } from "../../../features/store-language/i18n.ts";
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Spin, Empty, Button, Tooltip } from "antd";
import DirectionalIcon from "../../../features/navigation/DirectionalIcon.tsx";
import { ArrowsLeftRight, CaretDown, Heart, Receipt, SquaresFour } from "@phosphor-icons/react";
import { CheckOutlined } from "@ant-design/icons";
import ShopBreadcrumb from "../ShopBreadcrumb.tsx";
import AuthenticatedImage, {
  AuthenticatedImagePlaceholder,
} from "../../shared/AuthenticatedImage.tsx";
import CatalogOrderQuantity from "../Orders/CatalogOrderQuantity.tsx";
import { canViewAgencyInventory, canWriteAgencyCommerce } from "../../../utils/agencySession.ts";
import { useCatalogOrder } from "../Orders/useCatalogOrder.tsx";
import { isAgencyShopSession } from "../Orders/shopSession.ts";
import "./SchemaPreview.css";
import "../Orders/CatalogOrderControls.css";
import { compareDimensionPriority, getStoreCategories, getStorePreview } from "../storefrontService.ts";
import { findCategoryPath, type StoreCategoryNode } from "../Category/categoryTree.ts";
import { buildCategoryPath } from "../categoryUrl.ts";
import AppFeedback from "../../shared/AppFeedback.tsx";
import VariantDetails, { type VariantDetailsData } from "./VariantDetails.tsx";
import VariantImageChoices, { VariantImage } from "./VariantImageChoices.tsx";
import { buildProductPath, buildVariantPath } from "../productUrl.ts";
import { useVariantFavorites } from "../Favorites/useVariantFavorites.ts";
import DimensionSearch from "./DimensionSearch.tsx";


function formatStockLabel(value: number | null | undefined): string {
  if (value == null) { return "-"; }
  const stock = Math.max(0, value ?? 0);
  if (stock === 0) {
    return t("ناموجود");
  }

  return formatRugCount(stock);
}

interface DimensionItem {
  patternId: number;
  addressTitle: string;
  dimension: string;
  code?: string;
  images?: VariantImage[];
  fileId?: string | number | null;
  availableTakhteStock?: number;
  isAvailable?: boolean | null;
  displayPriority?: number;
}

interface ColorVariant {
  fileId: string | number | null;
  colorTitle: string;
  englishTitle: string;
  hexCode: string;
  dimensions: DimensionItem[];
}

interface ProductResponse {
  variant: VariantDetailsData | null;
  masterProductId: number;
  slug: string;
  name: string;
  seoTitle: string;
  englishName?: string;
  palette: string;
  englishPalette?: string;
  paletteId?: number;
  colors: ColorVariant[];
}

const SchemaPreview: React.FC = () => {
  const storeLanguage = useStoreLanguage();
  const location = useLocation();
  const variantFavorites = useVariantFavorites();
  const canViewInventory = canViewAgencyInventory();
  const navigate = useNavigate();
  const params = useParams<{
    schemaName?: string;
    masterProductId?: string;
    productSlug?: string;
    productKey?: string;
    variantId?: string;
    variantTitle?: string;
    slug?: string;
    color?: string;
  }>();
  const [searchParams] = useSearchParams();
  const categoryId = searchParams.get("categoryId");
  const categorySlug = searchParams.get("categorySlug") || params.slug;
  const [categoryPath, setCategoryPath] = useState<StoreCategoryNode[]>([]);
  const previewPriorityQuery = searchParams.toString();

  const [productData, setProductData] = useState<ProductResponse | null>(null);
  const [selectedColorIndex, setSelectedColorIndex] = useState<number | null>(
    null,
  );
  const [selectedDimensionFileId, setSelectedDimensionFileId] = useState<
    string | number | null
  >(null);
  const [selectedPatternId, setSelectedPatternId] = useState<number | null>(
    null,
  );
  const [desktopDimensionSelections, setDesktopDimensionSelections] = useState<
    Record<number, { fileId: string | number | null; patternId: number }>
  >({});
  const catalogOrder = useCatalogOrder();
  const [dimensionSearch, setDimensionSearch] = useState("");
  const [openVariantPanel, setOpenVariantPanel] = useState<"details" | null>(null);
  const [loading, setLoading] = useState(true);
  const selectionRef = useRef({ masterId: productData?.masterProductId, patternId: selectedPatternId });
  selectionRef.current = { masterId: productData?.masterProductId, patternId: selectedPatternId };

  useEffect(function () {
    let active = true;
    setCategoryPath([]);
    if (categorySlug) {
      getStoreCategories().then(function (categories) {
        if (active) setCategoryPath(findCategoryPath(categories, categorySlug));
      }).catch(function () {
        // The shared service reports the failure; keep the category landing link.
        if (active) setCategoryPath([]);
      });
    }
    return function () { active = false; };
  }, [categorySlug, storeLanguage]);

  useEffect(function () {
    let cancelled = false;
    async function fetchData() {
      const saved = selectionRef.current;
      setLoading(true);
      setDimensionSearch("");
      setOpenVariantPanel(null);
      setDesktopDimensionSelections({});
      setSelectedDimensionFileId(null);
      setSelectedPatternId(null);
      const requestedTitle = params.schemaName || params.productSlug || params.productKey;
      const legacyVariantId = params.variantId;
      const variantTitle = params.variantTitle;
      if (!requestedTitle || (legacyVariantId !== undefined && !/^[1-9]\d*$/.test(legacyVariantId))) {
        setProductData(null);
        setLoading(false);
        return;
      }
      try {
        const prioritySearchParams = new URLSearchParams(previewPriorityQuery);
        const response: ProductResponse = await getStorePreview(
          requestedTitle,
          params.masterProductId || prioritySearchParams.get("masterProductId"),
          prioritySearchParams,
          prioritySearchParams.get("from") === "showroom" ? undefined : params.slug,
          legacyVariantId,
          params.productKey,
          variantTitle,
        );
        if (cancelled) return;
        setProductData(response);
        if (!response.colors.length) return;
        const canonicalLocation = response.variant
          ? buildVariantPath(response.slug, response.variant.addressTitle, prioritySearchParams)
          : buildProductPath(response.slug, prioritySearchParams);
        if (`${window.location.pathname}${window.location.search}` !== canonicalLocation) {
          navigate(canonicalLocation, { replace: true });
        }
        const savedColorIndex = saved.masterId === response.masterProductId
          ? response.colors.findIndex(function (color) {
            return color.dimensions.some(function (dimension) { return dimension.patternId === saved.patternId; });
          }) : -1;
        const colorIndex = savedColorIndex >= 0 ? savedColorIndex : 0;
        setSelectedColorIndex(colorIndex);
        const dimensions = response.colors[colorIndex]?.dimensions || [];
        const selectedDimension = dimensions.find(function (dimension) {
          return savedColorIndex >= 0 && dimension.patternId === saved.patternId;
        }) || dimensions.find(function (dimension) {
          return Boolean(dimension.fileId) && dimension.isAvailable !== false;
        }) || dimensions[0];
        setSelectedDimensionFileId(selectedDimension?.fileId ?? null);
        setSelectedPatternId(selectedDimension?.patternId ?? null);
        if (selectedDimension) {
          setDesktopDimensionSelections({
            [colorIndex]: { fileId: selectedDimension.fileId ?? null, patternId: selectedDimension.patternId },
          });
        }
      } catch (error) {
        if (cancelled) return;
        setProductData(null);
        AppFeedback.httpError(error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void fetchData();
    return function () { cancelled = true; };
  }, [storeLanguage, params.schemaName, params.masterProductId, params.productSlug,
    params.variantId, params.productKey, params.variantTitle, params.slug,
    params.color, categoryId, previewPriorityQuery, navigate]);

  useEffect(function () {
    if (!productData || !productData.colors.length) return undefined;

    const pageTitle = [productData.variant?.title || productData.name, productData.palette]
      .filter(Boolean)
      .join(" - ");
    const title = `${pageTitle} | ${t("کاتالوگ رَج")}`;
    const description = t(
      "رنگ‌ها، ابعاد و موجودی {{value0}} را در {{value1}} مشاهده کنید.",
      { value0: pageTitle, value1: t("کاتالوگ رَج") },
    );
    const canonicalPath = productData.variant
      ? buildVariantPath(productData.slug, productData.variant.addressTitle)
      : buildProductPath(productData.slug);
    const canonicalUrl = new URL(canonicalPath, window.location.origin).toString();
    const previousTitle = document.title;

    function setMeta(attribute: "name" | "property", key: string, content: string) {
      let element = document.head.querySelector<HTMLMetaElement>(
        `meta[${attribute}="${key}"]`,
      );
      const previousContent = element?.getAttribute("content");
      const created = !element;
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attribute, key);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
      return function restoreMeta() {
        if (created) element?.remove();
        else if (previousContent === null || previousContent === undefined) {
          element?.removeAttribute("content");
        } else {
          element?.setAttribute("content", previousContent);
        }
      };
    }

    let canonical = document.head.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]',
    );
    const previousCanonical = canonical?.getAttribute("href");
    const createdCanonical = !canonical;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }

    document.title = title;
    canonical.href = canonicalUrl;
    const structuredData = document.createElement("script");
    structuredData.type = "application/ld+json";
    structuredData.dataset.rajProductSeo = "true";
    structuredData.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Product",
      name: productData.variant?.title || productData.name,
      description,
      sku: productData.variant?.code || String(productData.variant?.id || productData.masterProductId),
      isVariantOf: productData.variant ? {
        "@type": "ProductGroup",
        name: productData.name,
        productGroupID: String(productData.masterProductId),
        url: new URL(buildProductPath(productData.slug), window.location.origin).toString(),
      } : undefined,
      category: productData.palette || undefined,
      url: canonicalUrl,
      inLanguage: getLanguage(),
      additionalProperty: productData.palette ? [{
        "@type": "PropertyValue",
        name: t("پالت"),
        value: productData.palette,
      }] : undefined,
    });
    document.head.appendChild(structuredData);
    const restoreMeta = [
      setMeta("name", "description", description),
      setMeta("name", "robots", "index,follow,max-image-preview:large"),
      setMeta("name", "googlebot", "index,follow,max-image-preview:large,max-snippet:-1"),
      setMeta("property", "og:type", "product"),
      setMeta("property", "og:site_name", t("کاتالوگ رَج")),
      setMeta("property", "og:title", title),
      setMeta("property", "og:description", description),
      setMeta("property", "og:url", canonicalUrl),
      setMeta("name", "twitter:card", "summary_large_image"),
      setMeta("name", "twitter:title", title),
      setMeta("name", "twitter:description", description),
    ];

    return function cleanupSeoMetadata() {
      document.title = previousTitle;
      restoreMeta.forEach(function (restore) {
        restore();
      });
      structuredData.remove();
      if (createdCanonical) canonical?.remove();
      else if (previousCanonical === null || previousCanonical === undefined) {
        canonical?.removeAttribute("href");
      } else {
        canonical?.setAttribute("href", previousCanonical);
      }
    };
  }, [productData, storeLanguage]);

  const handleColorClick = (index: number) => {
    setSelectedColorIndex(index);
    setDimensionSearch("");
    const firstAvailableDimension = productData?.colors[index]?.dimensions.find(
      (dimension) =>
        Boolean(dimension.fileId) &&
        dimension.isAvailable !== false,
    );
    setSelectedDimensionFileId(firstAvailableDimension?.fileId ?? null);
    setSelectedPatternId(firstAvailableDimension?.patternId ?? null);
  };

  const handleDesktopColorClick = (index: number) => {
    if (selectedColorIndex === index) {
      return;
    }

    const firstAvailableDimension = productData?.colors[index]?.dimensions.find(
      (dimension) => {
        return dimension.isAvailable !== false;
      },
    );
    const savedSelection = desktopDimensionSelections[index];
    setSelectedColorIndex(index);
    setDimensionSearch("");
    setSelectedDimensionFileId(savedSelection?.fileId ?? null);
    setSelectedPatternId(
      savedSelection?.patternId ?? firstAvailableDimension?.patternId ?? null,
    );
  };

  const handleDimensionClick = (dim: DimensionItem) => {
    setSelectedDimensionFileId(dim.fileId ?? null);
    setSelectedPatternId(dim.patternId);
    if (selectedColorIndex !== null) {
      setDesktopDimensionSelections((previousSelections) => {
        return {
          ...previousSelections,
          [selectedColorIndex]: {
            fileId: dim.fileId ?? null,
            patternId: dim.patternId,
          },
        };
      });
    }
  };

  // Helper function to get first non-null fileId from dimensions
  const getFirstValidFileId = (dimensions: DimensionItem[]): string | number | null => {
    const firstValid = dimensions.find(
      (dim) => dim.fileId !== null && dim.fileId !== undefined,
    );
    return firstValid?.fileId || null;
  };

  async function addToOrder(patternId: number, quantity: number) {
    if (!isAgencyShopSession()) { navigate("/login"); return; }
    await catalogOrder.add(patternId, quantity);
  }

  if (loading) {
    return (
      <>
        <ShopBreadcrumb
          className="shop-breadcrumb--products"
          hierarchy
          items={[{ title: t("محصولات") }, { href: "/", title: t("خانه") }]}
        />
        <div className="catalog-loading">
          <Spin size="large" />
        </div>
      </>
    );
  }

  if (!productData || !productData.colors?.length) {
    return (
      <>
        <ShopBreadcrumb
          className="shop-breadcrumb--products"
          hierarchy
          items={[{ title: t("محصولات") }, { href: "/", title: t("خانه") }]}
        />
        <div className="catalog-empty">
          <Empty description={t("محصولی یافت نشد")} />
        </div>
      </>
    );
  }

  // Get dimensions for selected color, or all dimensions if none selected
  const selectedColor =
    selectedColorIndex !== null ? productData.colors[selectedColorIndex] : null;

  const displayDimensions: DimensionItem[] = selectedColor
    ? selectedColor.dimensions
    : productData.colors.flatMap((c) => c.dimensions);

  // Normalize dimension string for comparison (handle * and x, trim whitespace, normalize format)
  const normalizeDimension = (dim: string): string => {
    return dim
      .trim()
      .replace(/[۰-۹]/g, function (digit) {
        return String("۰۱۲۳۴۵۶۷۸۹".indexOf(digit));
      })
      .replace(/[٠-٩]/g, function (digit) {
        return String("٠١٢٣٤٥٦٧٨٩".indexOf(digit));
      })
      .replace(/×/g, "*")
      .replace(/x/gi, "*")
      .replace(/\s+/g, "");
  };

  const sortedDimensions = [...displayDimensions].sort(compareDimensionPriority);
  const sizeCountLabel = formatSizeCount(sortedDimensions.length);

  const filteredDimensions = sortedDimensions.filter((dimension) => {
    const query = normalizeDimension(dimensionSearch);
    if (!query) {
      return true;
    }
    const normalizedDimension = normalizeDimension(dimension.dimension);
    if (normalizedDimension.includes(query)) {
      return true;
    }

    const queryParts = query.split("*");
    if (queryParts.length !== 2 || queryParts.some((part) => !part)) {
      return false;
    }

    return normalizedDimension.includes(`${queryParts[1]}*${queryParts[0]}`);
  });
  const heroFileId =
    selectedDimensionFileId ??
    (selectedColor ? getFirstValidFileId(selectedColor.dimensions) : null);
  const selectedVariant = selectedColor?.dimensions.find(function (dimension) { return dimension.patternId === selectedPatternId; });
  const favoriteVariantId = productData.variant?.id ?? selectedVariant?.patternId;
  function renderFavoriteButton(productVariantId: number | undefined) {
    if (!productVariantId) return null;
    const isFavorite = variantFavorites.favoriteIds.has(productVariantId);
    const isPending = variantFavorites.pendingIds.has(productVariantId);
    const label = isFavorite ? t("حذف از علاقه‌مندی‌ها") : t("افزودن به علاقه‌مندی‌ها");
    return (
      <Tooltip title={label}>
        <button
          type="button"
          className={`schema-preview-favorite-button${isFavorite ? " is-favorite" : ""}`}
          disabled={isPending || (isAgencyShopSession() && !variantFavorites.ready)}
          aria-label={label}
          aria-pressed={isFavorite}
          onClick={function (event) {
            event.stopPropagation();
            if (!isAgencyShopSession()) {
              navigate("/login", { state: { returnTo: location.pathname + location.search } });
              return;
            }
            void variantFavorites.toggle(productVariantId);
          }}
          onKeyDown={function (event) { event.stopPropagation(); }}
        >
          {isPending ? <Spin size="small" /> : <Heart size={22} weight={isFavorite ? "fill" : "regular"} />}
        </button>
      </Tooltip>
    );
  }
  function quantityFor(variantId: number) {
    return catalogOrder.order?.items.find(function (line) {
      return line.productVariantId === variantId;
    })?.quantity ?? 0;
  }
  const orderLabel = catalogOrder.order?.title?.trim() || catalogOrder.destinationTitle || t("سفارش بدون عنوان");
  const quantityDisabled = catalogOrder.readFailed || (isAgencyShopSession() && !canWriteAgencyCommerce());

  return (
    <>
      {catalogOrder.dialog}
      <ShopBreadcrumb
        className="shop-breadcrumb--products"
        variant={catalogOrder.hasDestination ? "order" : undefined}
        hierarchy
        actions={productData.variant ? (
          <Link
            className="schema-master-link schema-master-link--breadcrumb"
            to={buildProductPath(productData.slug, searchParams)}
          >
            <SquaresFour
              size={18}
              weight="bold"
              aria-hidden="true"
            />
            <span>{t("رنگ‌های دیگر")}</span>
          </Link>
        ) : null}
        context={catalogOrder.hasDestination ? (
          <div
            className="catalog-order-context"
            dir={getDirection()}
          >
            <Receipt
              className="catalog-order-context__icon"
              size={16}
              aria-hidden="true"
            />
            <span className="catalog-order-context__label">{t("سفارش جاری")}:</span>
            <Button
              type="text"
              className="catalog-order-context__name"
              title={orderLabel}
              disabled={catalogOrder.pending || !catalogOrder.orderId}
              onClick={function () { navigate(`/account/orders/${catalogOrder.orderId}`); }}
            >
              <bdi>{orderLabel}</bdi>
            </Button>
            <Button
              type="text"
              className="catalog-order-context__change"
              icon={<ArrowsLeftRight size={16} aria-hidden="true" />}
              loading={catalogOrder.changingOrder}
              disabled={catalogOrder.pending || !canWriteAgencyCommerce()}
              onClick={catalogOrder.changeOrder}
            >
              {t("تغییر سفارش")}
            </Button>
            {catalogOrder.readFailed ? (
              <Button
                type="text"
                loading={catalogOrder.pending}
                disabled={catalogOrder.pending}
                onClick={catalogOrder.reload}
              >
                {t("تلاش مجدد")}
              </Button>
            ) : null}
          </div>
        ) : null}
        items={[
          { title: productData.variant?.title || productData.name },
          ...(productData.variant ? [{
            href: buildProductPath(productData.slug, searchParams),
            title: productData.name,
          }] : []),
          ...(categoryPath.length ? categoryPath.slice().reverse().map(function (category) {
            return { href: buildCategoryPath(category.slug), title: category.name };
          }) : [{ href: "/products", title: t("محصولات") }]),
          { href: "/", title: t("خانه") },
        ]}
      />

      <main
        className={`schema-preview-desktop${productData.variant ? " schema-preview--variant" : ""}`}
        dir={getDirection()}
      >
        <div className="schema-desktop-layout">
          <aside className="schema-desktop-info">
            <header className="schema-desktop-heading">
              <h1>{productData.variant?.title || productData.name}</h1>
            </header>

            {productData.variant && productData.variant.attributes.length > 0 && (
              <>
                <button
                  type="button"
                  className="schema-variant-panel-toggle"
                  aria-expanded={openVariantPanel === "details"}
                  aria-controls="schema-variant-details-desktop"
                  onClick={function () {
                    setOpenVariantPanel(openVariantPanel === "details" ? null : "details");
                  }}
                >
                  <span>{t("مشخصات")}</span>
                  <CaretDown size={18} aria-hidden="true" />
                </button>
                <div id="schema-variant-details-desktop" hidden={openVariantPanel !== "details"}>
                  <VariantDetails variant={productData.variant} />
                </div>
              </>
            )}
            <div className="schema-desktop-dimension-header">
              <DimensionSearch
                alwaysOpen
                key={`desktop-${productData.masterProductId}`}
                clearLabel={t("پاک کردن جستجو")}
                direction={getDirection()}
                label={t("جست‌وجوی ابعاد")}
                placeholder={t("جستجو در میان {{value0}}", { value0: sizeCountLabel })}
                value={dimensionSearch}
                onChange={setDimensionSearch}
              />
            </div>

            <div
              className="schema-desktop-dimensions"
              data-inventory-visible={canViewInventory}
            >
              <div className="schema-desktop-dimensions-labels">
                <span>{t("ابعاد")}</span>
                {canViewInventory && <span>{t("موجودی")}</span>}
                <span>{t("تعداد")}</span>
              </div>
              {filteredDimensions.map((dimension) => {
                const stock = dimension.availableTakhteStock;
                const isSelected = dimension.patternId === selectedPatternId;
                return (
                  <div
                    className={`schema-desktop-dimension-row ${isSelected ? "selected" : ""}`}
                    key={dimension.patternId}
                  >
                    <button
                      type="button"
                      onClick={() => handleDimensionClick(dimension)}
                    >
                      <bdi dir="ltr">{formatDimension(dimension.dimension)}</bdi>
                    </button>
                    {canViewInventory && (
                      <span className={dimension.isAvailable === false ? "unavailable" : "available"}>
                        {dimension.isAvailable === false ? t("ناموجود") : formatStockLabel(stock)}
                      </span>
                    )}
                    <CatalogOrderQuantity
                      quantity={quantityFor(dimension.patternId)}
                      pending={catalogOrder.pending && catalogOrder.activeVariant === dimension.patternId}
                      disabled={quantityDisabled}
                      unavailable={dimension.isAvailable === false}
                      label={dimension.dimension}
                      onChange={function (delta) {
                        return addToOrder(dimension.patternId, delta);
                      }}
                    />
                  </div>
                );
              })}
              {filteredDimensions.length === 0 && (
                <div className="schema-desktop-empty">{t("ابعادی پیدا نشد.")}</div>
              )}
            </div>

          </aside>

          <section className="schema-desktop-gallery" aria-label={t("رنگ‌های نقشه")}>
            {productData.colors.map((color, index) => {
              const isSelected = selectedColorIndex === index;
              const savedSelection = desktopDimensionSelections[index];
              const displayedVariant = color.dimensions.find(function (dimension) {
                return dimension.patternId === savedSelection?.patternId;
              }) || color.dimensions.find(function (dimension) {
                return Boolean(dimension.fileId);
              }) || color.dimensions[0];
              const displayFileId = savedSelection ? savedSelection.fileId : displayedVariant?.fileId;
              return (
                <article
                  className={`schema-desktop-color-card ${isSelected ? "selected" : ""}`}
                  key={`${color.colorTitle}-${index}`}
                  onClick={() => handleDesktopColorClick(index)}
                >
                  <div className="schema-desktop-color-image">
                    {renderFavoriteButton(displayedVariant?.patternId)}
                    {displayFileId ? (
                      <AuthenticatedImage
                        fileId={displayFileId.toString()}
                        height={500}
                        thumbnailHeight={500}
                        thumbPreviewMode={true}
                        alt={`${productData.name} - ${color.colorTitle}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <AuthenticatedImagePlaceholder animated={false} />
                    )}
                  </div>
                  <footer>
                    <div className="schema-desktop-color-summary">
                      <span
                        className="schema-desktop-color-dot"
                        style={{ backgroundColor: `#${color.hexCode}` }}
                      />
                      <div>
                        <strong>{color.colorTitle}</strong>
                        <small>{color.englishTitle}</small>
                        {displayedVariant && (
                          <span className="schema-desktop-color-size">
                            {t("ابعاد (متر)")}: <bdi dir="ltr">{formatDimension(displayedVariant.dimension)}</bdi>
                          </span>
                        )}
                      </div>
                      {isSelected && <CheckOutlined />}
                    </div>
                    {!productData.variant && displayedVariant?.addressTitle && (
                      <Link
                        className="schema-variant-link"
                        to={buildVariantPath(productData.slug, displayedVariant.addressTitle, searchParams)}
                        aria-label={t("مشاهده جزئیات {{value0}}", {
                          value0: `${color.colorTitle} · ${formatDimension(displayedVariant.dimension)}`,
                        })}
                        onClick={function (event) { event.stopPropagation(); }}
                      >
                        <span>{t("جزئیات کالا")}</span>
                        <DirectionalIcon
                          direction="forward"
                          size={16}
                          weight="bold"
                          aria-hidden="true"
                        />
                      </Link>
                    )}
                    <VariantImageChoices
                      images={displayedVariant?.images}
                      selected={displayFileId}
                      onSelect={function (fileId) {
                        setDesktopDimensionSelections(function (previous) {
                          return { ...previous, [index]: { fileId, patternId: displayedVariant.patternId } };
                        });
                        setSelectedColorIndex(index);
                        setSelectedPatternId(displayedVariant.patternId);
                        setSelectedDimensionFileId(fileId);
                      }}
                    />
                  </footer>
                </article>
              );
            })}
          </section>
        </div>
      </main>

      <main
        className={`schema-preview-page${productData.variant ? " schema-preview--variant" : ""}`}
        dir={getDirection()}
      >
        <section className="schema-visual-stage" aria-label={t("پیش‌نمایش نقشه")}>
          <div className="schema-title-card">
            <h1>{productData.variant?.title || productData.name}</h1>
          </div>

          <div className="schema-hero-media">
            {renderFavoriteButton(favoriteVariantId)}
            {heroFileId ? (
              <AuthenticatedImage
                key={heroFileId}
                fileId={heroFileId.toString()}
                className="schema-hero-image"
                height={500}
                thumbnailHeight={500}
                thumbPreviewMode={true}
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            ) : (
              <AuthenticatedImagePlaceholder animated={false} />
            )}
          </div>

          <VariantImageChoices
            images={selectedVariant?.images}
            selected={heroFileId}
            onSelect={setSelectedDimensionFileId}
          />

          {!productData.variant && (
            <div className="schema-color-rail" aria-label={t("انتخاب رنگ")}>
              {productData.colors.map((color, index) => {
                const colorFileId = getFirstValidFileId(color.dimensions);
                const isSelected = selectedColorIndex === index;
                return (
                  <button
                    type="button"
                    key={`${color.colorTitle}-${index}`}
                    className={`schema-color-option ${isSelected ? "selected" : ""}`}
                    onClick={() => handleColorClick(index)}
                    aria-pressed={isSelected}
                  >
                    <span className="schema-color-thumb">
                      {colorFileId ? (
                        <AuthenticatedImage
                          fileId={colorFileId.toString()}
                          height={130}
                          thumbPreviewMode={false}
                          disablePreview={true}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <span
                          className="schema-color-swatch"
                          style={{ backgroundColor: `#${color.hexCode}` }}
                        />
                      )}
                      {isSelected && (
                        <span className="schema-color-check">
                          <CheckOutlined />
                        </span>
                      )}
                    </span>
                    <span className="schema-color-name">{color.colorTitle}</span>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        <section
          className="schema-configurator"
          aria-label={t("انتخاب مشخصات خرید")}
        >
          <div className="schema-sheet-handle" aria-hidden="true" />
          {productData.variant && productData.variant.attributes.length > 0 && (
            <>
              <button
                type="button"
                className="schema-variant-panel-toggle"
                aria-expanded={openVariantPanel === "details"}
                aria-controls="schema-variant-details-mobile"
                onClick={function () {
                  setOpenVariantPanel(openVariantPanel === "details" ? null : "details");
                }}
              >
                <span>{t("مشخصات")}</span>
                <CaretDown size={18} aria-hidden="true" />
              </button>
              <div id="schema-variant-details-mobile" hidden={openVariantPanel !== "details"}>
                <VariantDetails variant={productData.variant} />
              </div>
            </>
          )}
          <div className="schema-section-heading">
            <h2>{t("انتخاب از میان {{value0}}", { value0: sizeCountLabel })}</h2>
            <DimensionSearch
              key={productData.masterProductId}
              clearLabel={t("پاک کردن جستجو")}
              direction={getDirection()}
              label={t("جست‌وجوی ابعاد")}
              placeholder={t("جست‌وجوی ابعاد")}
              value={dimensionSearch}
              onChange={setDimensionSearch}
            />
          </div>

          <div
            className="schema-dimensions-rail"
          >
            {filteredDimensions.map((dimension) => {
              const stock = dimension.availableTakhteStock;
              const isUnavailable = canViewInventory && dimension.isAvailable === false;
              const isSelected = dimension.patternId === selectedPatternId;
              return (
                <button
                  type="button"
                  key={dimension.patternId}
                  className={`schema-dimension-option ${isSelected ? "selected" : ""} ${isUnavailable ? "unavailable" : ""}`}
                  onClick={() => handleDimensionClick(dimension)}
                  aria-pressed={isSelected}
                >
                  <strong><bdi dir="ltr">{formatDimension(dimension.dimension)}</bdi></strong>
                  {canViewInventory && (
                    <span>
                      {isUnavailable ? t("ناموجود") : formatStockLabel(stock)}
                    </span>
                  )}
                </button>
              );
            })}
            {filteredDimensions.length === 0 && (
              <div className="schema-dimension-empty">{t("ابعادی مطابق جست‌وجوی شما پیدا نشد.")} </div>
            )}
          </div>


          <div className="schema-mobile-purchase-actions">
            <div className="schema-purchase-row">
              <div className="schema-quantity-control">
                <CatalogOrderQuantity
                  quantity={selectedPatternId ? quantityFor(selectedPatternId) : 0}
                  pending={catalogOrder.pending && catalogOrder.activeVariant === selectedPatternId}
                  disabled={!selectedPatternId || quantityDisabled}
                  unavailable={selectedVariant?.isAvailable === false}
                  label={selectedVariant?.dimension || t("کالا")}
                  onChange={async function (delta) {
                    if (selectedPatternId) await addToOrder(selectedPatternId, delta);
                  }}
                />
              </div>
            </div>
            {!productData.variant && selectedVariant?.addressTitle && (
              <Link
                className="schema-variant-link schema-variant-link--mobile"
                dir={getDirection()}
                to={buildVariantPath(
                  productData.slug,
                  selectedVariant.addressTitle,
                  searchParams,
                )}
              >
                <span>{t("جزئیات کالا")}</span>
                <DirectionalIcon
                  className="schema-variant-link__arrow"
                  direction="forward"
                  size={18}
                  weight="bold"
                  aria-hidden="true"
                />
              </Link>
            )}
            {productData.variant && (
              <Link
                className="schema-variant-link schema-variant-link--mobile"
                dir={getDirection()}
                to={buildProductPath(productData.slug, searchParams)}
              >
                <span>{t("رنگ‌های دیگر")}</span>
                <SquaresFour
                  className="schema-variant-link__arrow"
                  size={18}
                  weight="bold"
                  aria-hidden="true"
                />
              </Link>
            )}
          </div>
        </section>
      </main>

    </>
  );
};

export default SchemaPreview;
